// Login MFA challenge (Phase 4.5 enforcement gate). An authenticated session
// whose cookie is MFA-pending (mfaSatisfied=false) is routed here by
// RequireAuth before the app shell mounts. Two cases:
//   - Already enrolled  → enter a TOTP code (or a recovery code) to clear the gate.
//   - Tenant requires MFA but not enrolled → enroll now (QR + verify), which
//     also clears the gate and shows one-time recovery codes.
// On success we re-probe /api/me; the re-issued cookie reports mfaSatisfied=true
// and the render below redirects into the app.

import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { Loader } from '@progress/kendo-react-indicators';
import { QRCodeSVG } from 'qrcode.react';
import type { MfaEnrollBeginResponse } from '@shared/api-types';
import { ApiError } from '../../lib/apiClient';
import { beginMfaEnroll, completeMfaEnroll, verifyMfa } from '../../lib/settingsApi';
import { useAuth } from '../../lib/auth';
import { capturedReturnTo } from '../../lib/capturedPage';

const pageStyle: React.CSSProperties = {
  display: 'grid',
  placeItems: 'center',
  minHeight: '100vh',
  padding: 24,
  background: 'var(--bg)',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  padding: 28,
  border: '1px solid var(--border)',
  borderRadius: 12,
  background: 'var(--bg-elev)',
};

export function MfaRequired() {
  const { state, refresh, signOut } = useAuth();
  const location = useLocation();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [enroll, setEnroll] = useState<MfaEnrollBeginResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  if (state.status === 'unknown') {
    return (
      <div style={pageStyle}>
        <Loader size="large" type="converging-spinner" />
      </div>
    );
  }
  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace />;
  }
  // Gate cleared (just now, or already when navigated here directly) → back to
  // the page the visitor asked for, whole: path, query string and fragment.
  if (state.user.mfaSatisfied) {
    return <Navigate to={capturedReturnTo(location.state)} replace />;
  }

  const enrolled = state.user.mfaEnrolled;

  // --- Enrolled: verify a TOTP / recovery code ---
  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(code.trim());
      await refresh(); // cookie now mfa-ok=true → render redirects into the app
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'That code did not verify. Try again, or use a recovery code.'
          : 'Verification failed. Please retry.',
      );
    } finally {
      setBusy(false);
    }
  };

  // --- Not enrolled (tenant requires MFA): enroll now ---
  const startEnroll = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnroll(await beginMfaEnroll());
    } catch {
      setError('Could not start enrollment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enroll) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await completeMfaEnroll({ secretBase32: enroll.secretBase32, code: code.trim() });
      setRecoveryCodes(resp.recoveryCodes);
      setEnroll(null);
      setCode('');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? 'That code did not verify. Please try again.'
          : 'Enrollment failed. Please retry.',
      );
    } finally {
      setBusy(false);
    }
  };

  const finishRecovery = async () => {
    setRecoveryCodes(null);
    await refresh(); // cookie was re-issued mfa-ok=true at enroll-complete
  };

  const heading = (text: string) => (
    <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 600 }}>{text}</h2>
  );
  const errorBlock = error && (
    <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }} role="alert">{error}</div>
  );
  const signOutLink = (
    <button
      type="button"
      onClick={() => void signOut()}
      style={{
        display: 'block', marginTop: 16, background: 'none', border: 'none', padding: 0,
        color: 'var(--fg-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
      }}
    >
      Sign out
    </button>
  );

  // 1) One-time recovery-code display after enrollment.
  if (recoveryCodes) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          {heading('Save your recovery codes')}
          <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 0 }}>
            Store these somewhere safe — they are shown <strong>only once</strong> and let you
            sign in if you lose your authenticator.
          </p>
          <ul
            style={{
              fontFamily: 'ui-monospace, Consolas, monospace',
              columns: 2, padding: 0, margin: '0 0 16px', listStyle: 'none',
            }}
          >
            {recoveryCodes.map((c) => (
              <li key={c} style={{ padding: '4px 0' }}>{c}</li>
            ))}
          </ul>
          <Button themeColor="primary" onClick={() => void finishRecovery()} data-testid="mfa-recovery-done">
            I&apos;ve saved them — continue
          </Button>
        </div>
      </div>
    );
  }

  // 2) Enrollment-required, mid-enrollment: QR + verify.
  if (!enrolled && enroll) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          {heading('Set up two-factor authentication')}
          <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 0 }}>
            Your organization requires MFA. Scan this QR code with your authenticator app,
            then enter the 6-digit code.
          </p>
          <div style={{ display: 'grid', placeItems: 'center', margin: '8px 0 16px' }}>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
              <QRCodeSVG value={enroll.otpAuthUri} size={184} marginSize={1} aria-label="MFA enrollment QR code" />
            </div>
          </div>
          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 12, color: 'var(--fg-muted)', fontWeight: 600, cursor: 'pointer' }}>
              Can&apos;t scan it? Enter this key manually
            </summary>
            <code style={{ display: 'block', fontSize: 12, marginTop: 8, wordBreak: 'break-all' }}>
              {enroll.secretBase32}
            </code>
          </details>
          <form onSubmit={(e) => void submitEnroll(e)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(String(e.value ?? '').replace(/\D/g, '').slice(0, 6))}
              data-testid="mfa-enroll-code"
              aria-label="6-digit authentication code"
              placeholder="6-digit code"
              style={{ fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', maxWidth: 200 }}
            />
            {errorBlock}
            <div>
              <Button themeColor="primary" type="submit" disabled={busy || code.length !== 6} data-testid="mfa-enroll-verify">
                {busy ? 'Verifying…' : 'Verify and enable'}
              </Button>
            </div>
          </form>
          {signOutLink}
        </div>
      </div>
    );
  }

  // 3a) Enrollment-required, idle.
  if (!enrolled) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          {heading('Two-factor authentication required')}
          <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 0 }}>
            Your organization requires MFA to continue. Set up an authenticator app now.
          </p>
          {errorBlock}
          <Button themeColor="primary" disabled={busy} onClick={() => void startEnroll()} data-testid="mfa-enroll-start">
            {busy ? 'Starting…' : 'Set up authenticator app'}
          </Button>
          {signOutLink}
        </div>
      </div>
    );
  }

  // 3b) Enrolled: verify challenge.
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {heading('Enter your authentication code')}
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 0 }}>
          Open your authenticator app and enter the 6-digit code. You can also enter one of your
          recovery codes.
        </p>
        <form onSubmit={(e) => void submitVerify(e)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            value={code}
            onChange={(e) => setCode(String(e.value ?? ''))}
            data-testid="mfa-verify-code"
            aria-label="Authentication or recovery code"
            placeholder="6-digit or recovery code"
            autoFocus
            style={{ fontSize: 16, maxWidth: 260 }}
          />
          {errorBlock}
          <div>
            <Button themeColor="primary" type="submit" disabled={busy || code.trim().length === 0} data-testid="mfa-verify-submit">
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
          </div>
        </form>
        {signOutLink}
      </div>
    </div>
  );
}

export default MfaRequired;
